import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./ManageLicense.css";

const ManageLicense = () => {
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    expired: 0,
    disabled: 0,
  });

  const [licenses, setLicenses] = useState([]);
  const [filteredLicenses, setFilteredLicenses] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [statsLoading, setStatsLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);

  // রিসেট করা টোকেন পপআপে দেখানোর জন্য স্টেট
  const [generatedToken, setGeneratedToken] = useState(null);

  const API_BASE_URL = "/api/licenses";

  // =========================
  // ১. STATS FETCH
  // =========================
  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const res = await axios.get(`${API_BASE_URL}/stats`);
      const payload = res.data?.data || res.data;

      setStats({
        total: payload?.total || 0,
        active: payload?.active || 0,
        expired: payload?.expired || 0,
        disabled: payload?.disabled || 0,
      });
    } catch (err) {
      console.error("Stats API replication error:", err);
      setStats({ total: 0, active: 0, expired: 0, disabled: 0 });
    } finally {
      setStatsLoading(false);
    }
  };

  // =========================
  // ২. LIST FETCH
  // =========================
  const fetchLicenses = async () => {
    try {
      setTableLoading(true);
      const res = await axios.get(`${API_BASE_URL}/list?status=${selectedStatus}`);
      const rawData = res.data?.data || res.data;
      const data = Array.isArray(rawData) ? rawData : [];

      setLicenses(data);
      setFilteredLicenses(data);
    } catch (err) {
      console.error("License table synchronization error:", err);
      setLicenses([]);
      setFilteredLicenses([]);
    } finally {
      setTableLoading(false);
    }
  };

  // =========================
  // ৩. QUICK STATUS CHANGE (Suspend / Activate)
  // =========================
  const handleStatusChange = async (id, currentStatus, actionType) => {
    let targetStatus = currentStatus;
    if (actionType === "suspend") targetStatus = "suspended";
    if (actionType === "activate") targetStatus = "active";

    const confirmMsg = `Are you sure you want to ${actionType} this license (ID: #${id})?`;
    if (!window.confirm(confirmMsg)) return;

    try {
      setTableLoading(true);
      await axios.post(`${API_BASE_URL}/quick-status`, { id, status: targetStatus });
      alert(`License successfully ${actionType}ed!`);

      await fetchStats();
      await fetchLicenses();
    } catch (err) {
      console.error("Error executing quick action matrix:", err);
      alert(err.response?.data?.message || "Failed to update license state.");
      setTableLoading(false);
    }
  };

  // =========================
  // ৪. RESET TOKEN LOGIC (নতুন ফিচার)
  // =========================
  const handleResetToken = async (id, customerName) => {
    const confirmMsg = `Are you sure you want to RESET TOKEN for ${customerName} (ID: #${id})?\nThe old token will stop working immediately.`;
    if (!window.confirm(confirmMsg)) return;

    try {
      const res = await axios.post(`${API_BASE_URL}/reset-token`, { id });
      const newToken = res.data?.new_token;

      if (newToken) {
        setGeneratedToken({ id, token: newToken, customerName });
        await fetchLicenses();
      } else {
        alert("Token reset succeeded, but no token string was returned.");
      }
    } catch (err) {
      console.error("Error resetting token:", err);
      alert(err.response?.data?.message || "Failed to reset token.");
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchLicenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus]);

  // =========================
  // ৫. SEARCH FILTER
  // =========================
  useEffect(() => {
    const results = licenses.filter((item) =>
      (item.customer_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.machine_uuid || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.network_name || "").toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredLicenses(results);
  }, [searchTerm, licenses]);

  const cards = [
    { title: "Total Licenses", value: stats.total, color: "#3b82f6", type: "all" },
    { title: "Active Licenses", value: stats.active, color: "#22c55e", type: "active" },
    { title: "Expired Contracts", value: stats.expired, color: "#f59e0b", type: "expired" },
    { title: "Suspended Licenses", value: stats.disabled, color: "#ef4444", type: "suspended" },
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return "Lifetime";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  return (
    <div className="license-table-container">

      {/* HEADER */}
      <div className="table-header-wrapper" style={{ marginBottom: "24px" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: "24px", fontWeight: "800", color: "#1e293b" }}>Manage Licenses</h2>
          <p className="subtitle" style={{ margin: "4px 0 0 0", color: "#64748b", fontSize: "14px" }}>
            Real-time subscriber state control. Renew, extend or freeze client licenses.
          </p>
        </div>
      </div>

      {/* RESET TOKEN RESULT POPUP / DISPLAY */}
      {generatedToken && (
        <div style={{
          background: "#f0fdf4",
          border: "1px solid #22c55e",
          borderRadius: "8px",
          padding: "16px",
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          <div>
            <h4 style={{ margin: 0, color: "#15803d" }}>🔑 Token Reset Successful for {generatedToken.customerName}</h4>
            <p style={{ margin: "4px 0 0 0", fontSize: "14px", color: "#166534" }}>
              New Token: <code style={{ background: "#dcfce7", padding: "2px 6px", borderRadius: "4px", fontWeight: "bold" }}>{generatedToken.token}</code>
            </p>
          </div>
          <button
            onClick={() => {
              navigator.clipboard.writeText(generatedToken.token);
              alert("Token copied to clipboard!");
            }}
            style={{
              padding: "6px 12px",
              background: "#16a34a",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "bold"
            }}
          >
            Copy Token
          </button>
        </div>
      )}

      {/* CARDS */}
      <div className="manage-license-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        {cards.map((c, i) => (
          <div
            key={i}
            className={`manage-license-card ${selectedStatus === c.type ? "manage-license-active-card-filter" : ""}`}
            style={{ borderTop: `4px solid ${c.color}` }}
            onClick={() => setSelectedStatus(c.type)}
          >
            <h3>{c.title}</h3>
            <p>{statsLoading ? "..." : c.value}</p>
            <span className="manage-license-filter-indicator">Click to filter</span>
          </div>
        ))}
      </div>

      {/* SEARCH BAR */}
      <div className="manage-license-table-actions-bar" style={{ marginTop: "24px" }}>
        <input
          type="text"
          placeholder={`Search ${selectedStatus !== "all" ? selectedStatus : ""} by client, network or UUID...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="manage-license-search-input"
        />

        <div className="manage-license-active-filter-badge">
          Showing Status: <strong>{selectedStatus.toUpperCase()}</strong>
        </div>
      </div>

      {/* DATA TABLE */}
      {tableLoading ? (
        <div className="manage-license-loading-spinner-wrapper">
          <div className="manage-license-spinner"></div>
          <p>Syncing Subscriptions Registry...</p>
        </div>
      ) : (
        <div className="manage-license-responsive-table-wrapper">
          <table className="manage-license-pro-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Customer Name</th>
                <th>Network Name</th>
                <th>Machine UUID (HWID)</th>
                <th>Plan Type</th>
                <th>Channel Limit</th>
                <th>Status</th>
                <th>Expiration</th>
                <th style={{ textAlign: "center", width: "320px" }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredLicenses.length > 0 ? (
                filteredLicenses.map((l) => (
                  <tr key={l.id}>
                    <td style={{ fontFamily: "monospace", color: "#64748b", fontWeight: "600" }}>#{l.id}</td>
                    <td style={{ fontWeight: "700", color: "#1e293b" }}>{l.customer_name || "N/A"}</td>
                    <td>{l.network_name || "N/A"}</td>
                    <td>
                      {l.machine_uuid ? (
                        <code style={{ fontSize: "12px", color: "#0284c7", fontFamily: "monospace", fontWeight: "600" }}>
                          {l.machine_uuid.length > 15 ? `${l.machine_uuid.substring(0, 15)}...` : l.machine_uuid}
                        </code>
                      ) : (
                        <span style={{ color: "#94a3b8", fontStyle: "italic", fontSize: "12px" }}>Pending enrollment...</span>
                      )}
                    </td>
                    <td style={{ textTransform: "capitalize", fontWeight: "500" }}>{l.license_type || "monthly"}</td>
                    <td style={{ fontFamily: "monospace", fontWeight: "bold", color: "#4f46e5" }}>
                      {l.channel_limit !== null ? `${l.channel_limit} CH` : "0 CH"}
                    </td>
                    <td>
                      <span className={`status-badge-${l.license_status || "trial"}`}>
                        {l.license_status || "trial"}
                      </span>
                    </td>
                    <td style={{ fontFamily: "monospace" }}>{formatDate(l.expiry_date)}</td>

                    {/* ACTION BUTTONS */}
                    <td style={{ textAlign: "center" }}>
                      <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>

                        <button
                          className="btn-action-renew"
                          onClick={() => navigate("/update-license", { state: { license: l } })}
                          style={{ padding: "4px 8px", fontSize: "12px", fontWeight: "bold", cursor: "pointer", background: "#0f172a", color: "#fff", border: "none", borderRadius: "4px" }}
                        >
                          Renew/Edit
                        </button>

                        {/* RESET TOKEN BUTTON */}
                        <button
                          className="btn-action-reset-token"
                          onClick={() => handleResetToken(l.id, l.customer_name)}
                          style={{ padding: "4px 8px", fontSize: "12px", fontWeight: "bold", cursor: "pointer", background: "#d97706", color: "#fff", border: "none", borderRadius: "4px" }}
                        >
                          Reset Token
                        </button>

                        {l.license_status !== "suspended" ? (
                          <button
                            className="btn-action-suspend"
                            onClick={() => handleStatusChange(l.id, l.license_status, "suspend")}
                            style={{ padding: "4px 8px", fontSize: "12px", fontWeight: "bold", cursor: "pointer", background: "#ef4444", color: "#fff", border: "none", borderRadius: "4px" }}
                          >
                            Suspend
                          </button>
                        ) : (
                          <button
                            className="btn-action-activate"
                            onClick={() => handleStatusChange(l.id, l.license_status, "activate")}
                            style={{ padding: "4px 8px", fontSize: "12px", fontWeight: "bold", cursor: "pointer", background: "#22c55e", color: "#fff", border: "none", borderRadius: "4px" }}
                          >
                            Activate
                          </button>
                        )}

                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" style={{ textAlign: "center", padding: "32px", color: "#64748b", fontFamily: "monospace" }}>
                    ⚠️ No licenses found matching criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* BACK BUTTON */}
      <div style={{ marginTop: "24px", display: "flex", justifyContent: "flex-start" }}>
        <button className="km-back-btn" onClick={() => navigate("/")}>
          ← Back to Dashboard
        </button>
      </div>

    </div>
  );
};

export default ManageLicense;